import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { X, Pencil, Trash2, ShieldCheck, Plus, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr, adminDelete } from "../lib/api";
import { ADMIN_SECTIONS } from "../lib/adminSections";

// Gestion des sous-administrateurs — réservée au super administrateur.
// Un sous-admin cumule une ou plusieurs sections cochées (ex. Ambassadeurs + Presse).
export function SubAdminManager({ onChanged }) {
  const [subs, setSubs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editSub, setEditSub] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/subadmins").then((r) => { setSubs(r.data); onChanged?.(); }).catch(() => {});
  }, [onChanged]);
  useEffect(() => { load(); }, [load]);

  const remove = async (s) => {
    if (!window.confirm(`Supprimer le compte de ${s.first_name} ${s.last_name} ? Il n'aura plus accès à l'administration.`)) return;
    try { if (!await adminDelete(`/admin/subadmins/${s.id}`)) return; toast.success("Sous-administrateur supprimé."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mb-10 border border-white/10 bg-surface p-5 sm:p-6" data-testid="subadmin-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-display text-xl font-700 text-white">
            <ShieldCheck size={18} className="text-cyan" /> Sous-administrateurs
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-ink-secondary">
            Déléguez une ou plusieurs sections de l'administration à un membre de l'équipe.
            Il ne verra et ne pourra modifier que les sections cochées (ex. Ambassadeurs + Marketing Presse).
          </p>
        </div>
        <button onClick={() => setShowCreate((v) => !v)} data-testid="subadmin-create-toggle"
          className="aca-btn-primary inline-flex items-center gap-2">
          <Plus size={14} /> {showCreate ? "Fermer" : "Créer un administrateur"}
        </button>
      </div>

      {showCreate && <SubAdminForm onDone={() => { setShowCreate(false); load(); }} />}

      <div className="mt-5 space-y-3">
        {subs.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-elevated p-4" data-testid={`subadmin-row-${s.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-600 text-white">{s.first_name} {s.last_name}</div>
              <div className="truncate text-xs text-ink-tertiary">{s.email}</div>
            </div>
            <div className="flex max-w-md flex-wrap gap-1.5">
              {(s.admin_permissions || []).map((p) => (
                <span key={p} className="rounded-full border border-cyan/30 bg-cyan/10 px-2.5 py-1 text-[10px] font-600 uppercase tracking-wide text-cyan" data-testid={`subadmin-perm-${s.id}-${p}`}>
                  {ADMIN_SECTIONS.find((x) => x.perm === p)?.label || p}
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditSub(s)} data-testid={`subadmin-edit-${s.id}`}
                className="aca-btn-secondary inline-flex items-center gap-1.5 text-white hover:text-cyan">
                <Pencil size={13} /> Modifier
              </button>
              <button onClick={() => remove(s)} data-testid={`subadmin-delete-${s.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 px-3 py-1.5 text-xs font-600 text-red-400 transition-colors hover:border-red-400">
                <Trash2 size={13} /> Supprimer
              </button>
            </div>
          </div>
        ))}
        {subs.length === 0 && (
          <p className="rounded-xl border border-dashed border-white/15 px-4 py-5 text-center text-sm text-ink-tertiary" data-testid="subadmin-empty">
            Aucun sous-administrateur. Créez un compte pour déléguer une partie de l'administration.
          </p>
        )}
      </div>

      {editSub && <SubAdminForm sub={editSub} onDone={() => { setEditSub(null); load(); }} onClose={() => setEditSub(null)} />}
    </div>
  );
}

function SubAdminForm({ sub, onDone, onClose }) {
  const editing = !!sub;
  const [f, setF] = useState({
    first_name: sub?.first_name || "", last_name: sub?.last_name || "",
    email: sub?.email || "", password: "",
    permissions: sub?.admin_permissions || [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const togglePerm = (p) => setF((s) => ({
    ...s,
    permissions: s.permissions.includes(p) ? s.permissions.filter((x) => x !== p) : [...s.permissions, p],
  }));

  const save = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim() || !f.last_name.trim()) return toast.error("Indiquez le prénom et le nom.");
    if (!f.permissions.length) return toast.error("Cochez au moins une section autorisée.");
    if (!editing && f.password.length < 6) return toast.error("Mot de passe trop court (min 6 caractères).");
    setSaving(true);
    try {
      if (editing) {
        const payload = { first_name: f.first_name, last_name: f.last_name, email: f.email, permissions: f.permissions };
        if (f.password) payload.password = f.password;
        await api.put(`/admin/subadmins/${sub.id}`, payload);
        toast.success("Droits du sous-administrateur mis à jour.");
      } else {
        await api.post("/admin/subadmins", {
          first_name: f.first_name.trim(), last_name: f.last_name.trim(),
          email: f.email.trim(), password: f.password, permissions: f.permissions,
        });
        toast.success(`Compte créé pour ${f.first_name} ${f.last_name} — communiquez-lui son identifiant et son mot de passe.`);
      }
      onDone();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };

  const body = (
    <form onSubmit={save} className={editing ? "space-y-4" : "mt-5 space-y-4 rounded-xl border border-white/10 bg-elevated p-5"} data-testid={editing ? "subadmin-edit-form" : "subadmin-create-form"}>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Prénom</span>
          <input required value={f.first_name} onChange={set("first_name")} data-testid="subadmin-first-name"
            className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Nom</span>
          <input required value={f.last_name} onChange={set("last_name")} data-testid="subadmin-last-name"
            className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
        </label>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">E-mail (identifiant)</span>
          <input required type="email" value={f.email} onChange={set("email")} data-testid="subadmin-email"
            className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">
            {editing ? "Nouveau mot de passe (vide = inchangé)" : "Mot de passe prédéfini"}
          </span>
          <input type="text" value={f.password} onChange={set("password")} data-testid="subadmin-password"
            placeholder={editing ? "Laisser vide pour conserver" : "Min 6 caractères"} required={!editing}
            className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm text-white placeholder:text-ink-tertiary outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
        </label>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">
          <KeyRound size={13} /> Sections autorisées (plusieurs possibles)
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="subadmin-permissions">
          {ADMIN_SECTIONS.map((s) => (
            <label key={s.perm} className={`flex cursor-pointer items-center gap-2.5 rounded-xl border p-3 text-sm transition-colors ${f.permissions.includes(s.perm) ? "border-cyan/50 bg-cyan/10 text-cyan" : "border-white/10 bg-surface text-ink-secondary hover:border-white/25"}`}
              data-testid={`subadmin-perm-${s.perm}`}>
              <input type="checkbox" checked={f.permissions.includes(s.perm)} onChange={() => togglePerm(s.perm)} className="h-4 w-4 accent-cyan" />
              {s.label}
            </label>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 pt-1">
        <button type="submit" disabled={saving} data-testid="subadmin-save"
          className="aca-btn-primary disabled:opacity-60">
          {saving ? "Enregistrement…" : editing ? "Enregistrer les droits" : "Créer le compte"}
        </button>
        {editing && (
          <button type="button" onClick={onClose} data-testid="subadmin-edit-cancel"
            className="aca-btn-secondary text-white">
            Annuler
          </button>
        )}
      </div>
    </form>
  );

  if (!editing) return body;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 aca-modal" data-testid="subadmin-edit-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-white/10 bg-elevated p-6 sm:p-8">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-display text-xl font-700 text-white">Droits de {sub.first_name} {sub.last_name}</h3>
          <button onClick={onClose} data-testid="subadmin-edit-close" className="text-ink-secondary hover:text-white"><X size={20} /></button>
        </div>
        {body}
      </motion.div>
    </div>
  );
}
